import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStreamAuth } from '../../hooks/useStreamAuth';
import { useProfile } from '../../contexts/ProfileContext';
import { useAppLoad } from '../../contexts/AppLoadContext';
import LoadingSpinner from './LoadingSpinner';
import spotlightService from '../../services/SpotlightService';

const ProfileRedirectHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading: authLoading } = useStreamAuth();
  const { loading: appLoadLoading, needsProfileSelection: appNeedsProfileSelection } = useAppLoad();
  const {
    profiles,
    currentProfile,
    hasLoadedProfiles,
    isLoading: profileLoading,
    error: profileError,
    loadProfiles,
    needsProfileSelection,
    isSwitchingProfiles,
    setActiveProfile,
  } = useProfile();

  // Track if we've permanently failed (to prevent infinite loops)
  const [hasPermanentlyFailed, setHasPermanentlyFailed] = useState(false);

  // Load profiles immediately when user is authenticated (don't wait for appload)
  // CRITICAL: Wait for SpotlightService initialization before loading profiles
  // This ensures client token is ready before profile API calls (especially important for Safari)
  useEffect(() => {
    const initializeAndLoadProfiles = async () => {
      if (isAuthenticated && user && !hasLoadedProfiles && !profileLoading && !profileError && !hasPermanentlyFailed) {
        console.log('🔧 ProfileRedirectHandler: Waiting for SpotlightService initialization...');
        
        // Wait for SpotlightService to finish initialization
        // This ensures the client token from Auth0 is properly set before we try to load profiles
        await spotlightService.initializationPromise;
        
        console.log('✅ ProfileRedirectHandler: SpotlightService initialized, loading profiles...');
        loadProfiles(false); // Not a manual retry
      }
    };
    
    initializeAndLoadProfiles();
  }, [isAuthenticated, user, hasLoadedProfiles, profileLoading, profileError, loadProfiles, hasPermanentlyFailed]);

  // Handle profile loading errors with retry logic (with circuit breaker)
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (profileError && !hasLoadedProfiles && !profileLoading && !hasPermanentlyFailed) {
      const isNetworkError = profileError.includes('Failed to fetch') || 
                            profileError.includes('NetworkError');
      const isTokenError = profileError.includes('client token refresh failed') ||
                          profileError.includes('client token missing') ||
                          profileError.includes('403');
      
      if (isNetworkError) {
        console.error('ProfileRedirectHandler: Network error - stopping retries');
        setHasPermanentlyFailed(true);
        setShowError(true);
        return;
      }

      if (retryCount >= maxRetries) {
        console.error('ProfileRedirectHandler: Max retries reached. Stopping.');
        setHasPermanentlyFailed(true);
        setShowError(true);
        return;
      }
      
      // Token errors are retryable — the backend session may still be valid and
      // a fresh client token can be obtained on the next attempt
      const delay = isTokenError ? 2000 * (retryCount + 1) : 5000 * (retryCount + 1);
      const retryTimer = setTimeout(() => {
        console.warn(`ProfileRedirectHandler: Retrying profile load (attempt ${retryCount + 1}/${maxRetries})${isTokenError ? ' [token error - will re-request]' : ''}`);
        setRetryCount(prev => prev + 1);
        loadProfiles(false);
      }, delay);

      return () => clearTimeout(retryTimer);
    }
  }, [profileError, hasLoadedProfiles, profileLoading, loadProfiles, retryCount, hasPermanentlyFailed]);

  // Auto-select the first profile instead of redirecting to profile selection page.
  //
  // Single-shot per profile id. A failed switch leaves currentProfile null and
  // re-renders (isLoading flips back to false), which used to re-arm this effect
  // forever — with the SpotlightService circuit breaker open that spins at
  // microtask speed and freezes the tab. The ref makes the attempt exactly-once
  // synchronously (before the promise settles), and a failure is terminal until
  // the user retries.
  const attemptedProfileIdRef = useRef(null);
  const [profileSelectFailed, setProfileSelectFailed] = useState(false);

  useEffect(() => {
    const profile = profiles[0];
    const profileId = profile && (profile._id || profile.id);
    if (
      isAuthenticated &&
      hasLoadedProfiles &&
      profile &&
      !currentProfile &&
      !profileLoading &&
      !isSwitchingProfiles &&
      !profileSelectFailed &&
      attemptedProfileIdRef.current !== profileId
    ) {
      attemptedProfileIdRef.current = profileId;
      Promise.resolve(setActiveProfile(profile))
        .then(ok => {
          if (ok === false) {
            console.error('ProfileRedirectHandler: Auto-select failed — stopping.');
            setProfileSelectFailed(true);
          }
        })
        .catch(err => {
          console.error('ProfileRedirectHandler: Auto-select threw — stopping.', err);
          setProfileSelectFailed(true);
        });
    }
  }, [
    isAuthenticated,
    hasLoadedProfiles,
    profiles,
    currentProfile,
    profileLoading,
    isSwitchingProfiles,
    profileSelectFailed,
    setActiveProfile,
  ]);

  // Handle profile-based redirects
  useEffect(() => {
    // Don't redirect if still loading auth or profiles
    if (authLoading || profileLoading) {
      return;
    }

    // Don't redirect if not authenticated
    if (!isAuthenticated) {
      return;
    }

    // Don't redirect if we haven't loaded profiles yet
    if (!hasLoadedProfiles) {
      return;
    }

    // Don't redirect if already on profile-related pages
    if (location.pathname === '/profiles') {
      return;
    }

    // Don't redirect if user is explicitly switching profiles
    if (isSwitchingProfiles) {
      return;
    }
  }, [
    authLoading,
    profileLoading,
    isAuthenticated,
    hasLoadedProfiles,
    location.pathname,
    needsProfileSelection,
    profiles.length,
    currentProfile,
    navigate,
    isSwitchingProfiles,
  ]);

  // Show error if permanent failure detected (stale token, or auto-select gave up)
  if ((showError && profileError) || profileSelectFailed) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#0a0a0a',
        color: 'white',
      }}>
        <div style={{
          maxWidth: '600px',
          padding: '40px',
          backgroundColor: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #333',
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '20px', color: '#ff4444' }}>
            ⚠️ Connection Error
          </h1>
          <p style={{ marginBottom: '20px', lineHeight: '1.6' }}>
            We're having trouble loading your profile. This usually happens when cached data becomes outdated.
          </p>
          <p style={{ marginBottom: '20px', fontSize: '14px', color: '#999' }}>
            Click the button below to clear your cache and try again.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '30px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                attemptedProfileIdRef.current = null;
                setProfileSelectFailed(false);
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
              }}
            >
              Clear Cache & Retry
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '12px 24px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking authentication
  if (authLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // If AppLoadContext indicates we need profile selection, show loading while auto-selecting
  if (appNeedsProfileSelection && location.pathname !== '/profiles') {
    return <LoadingSpinner message="Loading..." />;
  }

  // If authenticated but profiles are still loading, show loading
  if (isAuthenticated && (profileLoading || !hasLoadedProfiles)) {
    return <LoadingSpinner message="Loading profiles..." />;
  }

  // If authenticated and profiles are loaded, but no current profile yet (auto-selecting), show loading
  if (
    isAuthenticated &&
    hasLoadedProfiles &&
    profiles.length > 0 &&
    !currentProfile &&
    location.pathname !== '/profiles'
  ) {
    return <LoadingSpinner message="Loading..." />;
  }

  // Render children once everything is loaded and checked
  return children;
};

export default ProfileRedirectHandler;
